import { useState, useEffect, useCallback } from 'react';
import PRODUCT_API_URL, { PRODUCT_API_HEADERS } from '../../../../api/constants/productApi.constant.api';
import {
  parseBrands,
  parseModels,
  parseYears,
  parseVersions,
  parseFitments,
  getEanFromProducts,
} from '../../../../lib/product-api.parse.utils';

const FETCH_TIMEOUT = 8000;

const fetchWithTimeout = async (url, timeout = FETCH_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...PRODUCT_API_HEADERS,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

const useVehicleData = (isVisible, vehicle) => {
  const apiUrl = PRODUCT_API_URL();

  // Options state
  const [vendors, setVendors] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [versions, setVersions] = useState([]);
  const [fitments, setFitments] = useState([]);

  // EAN resolution state
  const [resolvedEans, setResolvedEans] = useState([]);
  const [loadingEans, setLoadingEans] = useState(false);

  // Loading state
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loadingFitments, setLoadingFitments] = useState(false);

  // Fetch vendors on mount
  useEffect(() => {
    if (!isVisible) return;

    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const response = await fetchWithTimeout(`${apiUrl}req=vendor`);
        const parsed = parseBrands(response);
        setVendors(parsed);
      } catch (error) {
        console.error('Error fetching vendors:', error);
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendors();
  }, [apiUrl, isVisible]);

  // Fetch models when vendor changes
  useEffect(() => {
    if (!vehicle?.vendor) {
      setModels([]);
      return;
    }

    const fetchModels = async () => {
      setLoadingModels(true);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vehicle.vendor)}&req=model`
        );
        const parsed = parseModels(response);
        setModels(parsed);
      } catch (error) {
        console.error('Error fetching models:', error);
        setModels([]);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchModels();
  }, [apiUrl, vehicle?.vendor]);

  // Fetch years when model changes
  useEffect(() => {
    if (!vehicle?.vendor || !vehicle?.model) {
      setYears([]);
      return;
    }

    const fetchYears = async () => {
      setLoadingYears(true);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vehicle.vendor)}&model=${encodeURIComponent(vehicle.model)}&req=year`
        );
        const parsed = parseYears(response);
        setYears(parsed);
      } catch (error) {
        console.error('Error fetching years:', error);
        setYears([]);
      } finally {
        setLoadingYears(false);
      }
    };

    fetchYears();
  }, [apiUrl, vehicle?.vendor, vehicle?.model]);

  // Fetch versions when year changes
  useEffect(() => {
    if (!vehicle?.vendor || !vehicle?.model || !vehicle?.year) {
      setVersions([]);
      return;
    }

    const fetchVersions = async () => {
      setLoadingVersions(true);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vehicle.vendor)}&model=${encodeURIComponent(vehicle.model)}&year=${encodeURIComponent(vehicle.year)}&req=version`
        );
        const parsed = parseVersions(response);
        setVersions(parsed);
      } catch (error) {
        console.error('Error fetching versions:', error);
        setVersions([]);
      } finally {
        setLoadingVersions(false);
      }
    };

    fetchVersions();
  }, [apiUrl, vehicle?.vendor, vehicle?.model, vehicle?.year]);

  // Fetch fitments when version changes
  useEffect(() => {
    if (!vehicle?.vendor || !vehicle?.model || !vehicle?.year || !vehicle?.version) {
      setFitments([]);
      return;
    }

    const fetchFitments = async () => {
      setLoadingFitments(true);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vehicle.vendor)}&model=${encodeURIComponent(vehicle.model)}&year=${encodeURIComponent(vehicle.year)}&version=${encodeURIComponent(vehicle.version)}&req=frontSizeDesignation`
        );
        const parsed = parseFitments(response);
        setFitments(parsed);
      } catch (error) {
        console.error('Error fetching fitments:', error);
        setFitments([]);
      } finally {
        setLoadingFitments(false);
      }
    };

    fetchFitments();
  }, [apiUrl, vehicle?.vendor, vehicle?.model, vehicle?.year, vehicle?.version]);

  // Resolve EANs when fitment is selected
  useEffect(() => {
    if (!vehicle?.vendor || !vehicle?.model || !vehicle?.year || !vehicle?.version || !vehicle?.fitment) {
      setResolvedEans([]);
      return;
    }

    const fetchEans = async () => {
      setLoadingEans(true);
      try {
        const response = await fetchWithTimeout(
          `${apiUrl}vendor=${encodeURIComponent(vehicle.vendor)}&model=${encodeURIComponent(vehicle.model)}&year=${encodeURIComponent(vehicle.year)}&version=${encodeURIComponent(vehicle.version)}&frontSizeDesignation=${encodeURIComponent(vehicle.fitment)}&products=true&articles=true`
        );
        setResolvedEans(getEanFromProducts(response));
      } catch (error) {
        console.error('Error resolving vehicle EANs:', error);
        setResolvedEans([]);
      } finally {
        setLoadingEans(false);
      }
    };

    fetchEans();
  }, [apiUrl, vehicle?.vendor, vehicle?.model, vehicle?.year, vehicle?.version, vehicle?.fitment]);

  const clearAll = useCallback(() => {
    setModels([]);
    setYears([]);
    setVersions([]);
    setFitments([]);
    setResolvedEans([]);
  }, []);

  return {
    vendors,
    models,
    years,
    versions,
    fitments,
    resolvedEans,
    loadingVendors,
    loadingModels,
    loadingYears,
    loadingVersions,
    loadingFitments,
    loadingEans,
    clearAll,
  };
};

export default useVehicleData;
